/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
'use client';

import { Clock, ExternalLink, Globe, MessageCircle, Share2, Video } from 'lucide-react';
import React from 'react';

import { useOpenMessage } from '../../hooks/useOpenMessage';
import { ContentRegistry } from '../registries';
import { ContentItemProps } from '../registries/ContentRegistry';

const LinkCard: React.FC<ContentItemProps> = ({ content, variant = 'card', onAction, className }) => {
  const isMinimal = variant === 'minimal';
  const isExpanded = variant === 'expanded';
  const { openMessage } = useOpenMessage();

  const handleClick = () => {
    onAction?.('inspect', content);
  };

  const handleChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    const vendorId = content.data?.vendorId || content.source?.author?.id;
    const vendorName = content.source?.author?.name;
    openMessage({
      vendorId,
      vendorName,
      contentContext: content,
    });
  };

  const domain = content.data?.domain || new URL(content.data?.url || '#').hostname;
  const linkType = content.data?.type || 'Article';
  const isVideo = linkType === 'Video';
  const duration = content.data?.duration;
  const readTime = content.data?.readTime;

  if (isMinimal) {
    return (
      <div
        className={`inspect-theme-bg-elevated rounded-lg p-3 cursor-pointer hover:inspect-theme-shadow transition-all ${className}`}
        onClick={handleClick}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1">
            {isVideo ? (
              <Video className="w-4 h-4 inspect-theme-primary" />
            ) : (
              <Globe className="w-4 h-4 inspect-theme-primary" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="inspect-theme-text font-medium text-sm line-clamp-2">{content.title}</h4>
            <div className="flex items-center gap-2 mt-1 text-xs inspect-theme-text-muted">
              <span>{domain}</span>
              {duration && <span>• {duration} min</span>}
              {readTime && <span>• {readTime} min read</span>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <article
      className={`inspect-theme-bg-elevated rounded-xl overflow-hidden cursor-pointer hover:inspect-theme-shadow-lg transition-all duration-300 ${className}`}
      onClick={handleClick}
    >
      {/* Preview Image */}
      {content.media?.thumbnail && (
        <div className="relative aspect-video overflow-hidden bg-gray-100">
          <img
            src={content.media.thumbnail.url}
            alt={content.title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />

          {/* Type Badge */}
          <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
            {isVideo ? <Video className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
            {linkType}
          </div>

          {/* Duration/Read Time */}
          {(duration || readTime) && (
            <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {duration ? `${duration} min` : `${readTime} min read`}
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className={`p-5 ${isExpanded ? 'space-y-4' : 'space-y-3'}`}>
        {/* Domain & Type */}
        <div className="flex items-center gap-2 text-sm">
          <Globe className="w-4 h-4 inspect-theme-text-muted" />
          <span className="inspect-theme-text-muted">{domain}</span>
          <span className="inspect-theme-text-muted">•</span>
          <span className="inspect-theme-primary">{linkType}</span>
        </div>

        {/* Title */}
        <h3 className={`font-semibold inspect-theme-text ${isExpanded ? 'text-xl' : 'text-lg'} line-clamp-2`}>
          {content.title}
        </h3>

        {/* Description */}
        {content.description && (
          <p className={`inspect-theme-text-muted ${isExpanded ? 'line-clamp-4' : 'line-clamp-2'} text-sm`}>
            {content.description}
          </p>
        )}

        {/* Additional Info */}
        {isExpanded && content.data && (
          <div className="flex flex-wrap gap-4 text-sm inspect-theme-text-muted">
            {content.data.difficulty && (
              <div>
                <span className="font-medium">Level:</span>
                <span className="ml-2">{content.data.difficulty}</span>
              </div>
            )}
            {content.data.language && (
              <div>
                <span className="font-medium">Language:</span>
                <span className="ml-2">{content.data.language}</span>
              </div>
            )}
            {content.engagement.views !== undefined && (
              <div>
                <span className="font-medium">Views:</span>
                <span className="ml-2">{content.engagement.views.toLocaleString()}</span>
              </div>
            )}
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          {content.tags.slice(0, 3).map((tag, index) => (
            <span key={index} className="inspect-theme-tag text-xs px-2 py-1 rounded-full">
              {tag}
            </span>
          ))}
        </div>

        {/* URL Preview */}
        <div className="p-3 inspect-theme-bg rounded-lg border inspect-theme-border">
          <p className="text-xs inspect-theme-text-muted truncate">{content.data?.url || '#'}</p>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAction?.('open', content);
            }}
            className="flex-1 inspect-theme-button-primary px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4 inline mr-2" />
            Open Link
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAction?.('share', content);
            }}
            className="p-2 inspect-theme-button-secondary rounded-lg transition-colors"
            title="Share"
          >
            <Share2 className="w-4 h-4" />
          </button>
          <button
            onClick={handleChat}
            className="p-2 inspect-theme-button-secondary rounded-lg transition-colors"
            title="Chat about this link"
          >
            <MessageCircle className="w-4 h-4" />
          </button>
        </div>
      </div>
    </article>
  );
};

// Auto-register with ContentRegistry
ContentRegistry.register('link', LinkCard);

export default LinkCard;
